import { useEffect, useRef, useState } from 'react';
import {
  Button,
  Input,
  Textarea,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { useToast } from '@vnetwork/ui/feedback';
import { submitWebsiteLead } from '@/api/leads';
import { PRODUCT_GROUP } from '@/lib/lead-product-group';
import { useMyContact } from '@/api/contact';
import { useAuth } from '@/auth/AuthProvider';
import { brand } from '@/config/brand';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
/** Vietnamese phone: a 10-digit number starting with 0 (e.g. 0987654321);
 *  spaces/dashes allowed and stripped before matching. */
const isValidPhone = (v: string) => /^0\d{9}$/.test(v.replace(/\D/g, ''));

interface ContactForm {
  name: string;
  workEmail: string;
  phone: string;
  comments: string;
}

const EMPTY: ContactForm = {
  name: '',
  workEmail: '',
  phone: '',
  comments: '',
};

/** Split a full name into first / last for the lead payload. */
function splitName(name: string): { firstname: string; lastname?: string } {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return { firstname: parts[0] ?? '', lastname: parts.slice(1).join(' ') || undefined };
}

/**
 * "Talk to an expert" — the Custom (Enterprise) plan contact form. Submits a
 * sales lead to DataCentral (/cms/submit, via submitWebsiteLead) instead of
 * self-serve provisioning; a rep follows up to scope a custom plan.
 */
export function PlanContactDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const { show } = useToast();
  const { user } = useAuth();
  const { data: contact, isFetched } = useMyContact();
  const [f, setF] = useState<ContactForm>(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const set = (patch: Partial<ContactForm>) => setF((prev) => ({ ...prev, ...patch }));

  // Pre-fill from the signed-in partner's DataCentral contact (falling back to
  // the account token). Seed once the contact query has settled so phone is
  // included when available. Email is authoritative (kept read-only).
  const seededRef = useRef(false);
  useEffect(() => {
    if (seededRef.current || (!isFetched && !user?.email)) return;
    seededRef.current = true;
    setF((prev) => ({
      ...prev,
      name: contact?.name || user?.name || '',
      workEmail: contact?.email || user?.email || '',
      phone: contact?.phone ?? '',
    }));
  }, [isFetched, contact, user?.email, user?.name]);

  const canSubmit = Boolean(
    f.name.trim() && EMAIL_RE.test(f.workEmail.trim()) && isValidPhone(f.phone) && !submitting,
  );

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await submitWebsiteLead({
        ...splitName(f.name),
        email: f.workEmail.trim(),
        phone: f.phone.trim(),
        interestedSolutions: [PRODUCT_GROUP.waap],
        // The interested service is the product-group (WAF V2); the custom-plan
        // intent + the user's note ride along in the free-text needs field.
        specificNeeds: ['WAAP - Custom Plan', f.comments.trim()].filter(Boolean).join(' — ') || undefined,
      });
      show({ tone: 'success', title: 'Request sent', description: 'Our team will contact you shortly.' });
      // Keep the pre-filled identity so a re-open still shows the account info.
      setF({
        ...EMPTY,
        name: contact?.name || user?.name || '',
        workEmail: contact?.email || user?.email || '',
        phone: contact?.phone ?? '',
      });
      onOpenChange(false);
    } catch {
      show({ tone: 'danger', title: 'Could not send your request', description: 'Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Talk to an expert</DialogTitle>
          <DialogDescription>
            Tell us about your needs and a specialist will scope a custom WAAP plan with you.
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
            <Field label="Name" required>
              {(p) => <Input {...p} value={f.name} onChange={(e) => set({ name: e.target.value })} />}
            </Field>
            <Field label="Work email" required description={`From your ${brand.shortName} account.`}>
              {(p) => <Input {...p} type="email" value={f.workEmail} disabled />}
            </Field>
            <Field label="Phone" required description="e.g. 0987654321.">
              {(p) => (
                <Input
                  {...p}
                  type="tel"
                  value={f.phone}
                  maxLength={15}
                  aria-invalid={f.phone.trim() !== '' && !isValidPhone(f.phone)}
                  onChange={(e) => set({ phone: e.target.value })}
                />
              )}
            </Field>
            <Field label="Comments">
              {(p) => <Textarea {...p} rows={3} value={f.comments} onChange={(e) => set({ comments: e.target.value })} />}
            </Field>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button disabled={!canSubmit} onClick={submit}>
            {submitting ? 'Sending…' : 'Talk to an expert'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
